import React from 'react';

import Loadable from 'react-loadable';

import type { AnalyticsEventPayload } from '@atlaskit/editor-common/analytics';
import type { Dispatch } from '@atlaskit/editor-common/event-dispatcher';
import type { PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { expValNoExposure } from '@atlaskit/tmp-editor-statsig/expVal';

import { makeDuplicateAndReplaceMauiApp } from './editor-actions/duplicate-and-replace-maui-app';
import { makeFinalizeMauiEmbedFromIndicator } from './editor-actions/finalize-maui-embed-from-indicator';
import { makeAddBackendRemixHistoryStackEntryCommand } from './editor-commands/addBackendRemixHistoryStackEntry';
import { clearRemixThinkingDecoration } from './editor-commands/clear-remix-thinking-decoration';
import { makeCloseRemixModal } from './editor-commands/close-remix-modal';
import { makeOpenRemixModal } from './editor-commands/open-remix-modal';
import { replaceMauiApp } from './editor-commands/replace-maui-app';
import { replaceWhiteboardEmbed } from './editor-commands/replace-whiteboard-embed';
import { makeStartRemixThinking } from './editor-commands/start-remix-thinking';
import { stopRemixThinking } from './editor-commands/stop-remix-thinking';
import {
	makeTrackVariantUpdateCompleteCommand,
	makeTrackVariantUpdateStartCommand,
} from './editor-commands/trackVariantUpdate';
import { makeUpdateRemixImageHistoryFieldIdCommand } from './editor-commands/updateRemixImageHistoryFieldId';
import type { MalleableUiPlugin, MalleableUiSharedState } from './malleableUiPluginType';
import { remixButtonDecoration } from './pm-plugins/decorations-remix-button';
import { createEditorViewCapturePlugin } from './pm-plugins/editor-view-capture-plugin';
import {
	createMauiDiagramPastePmPlugin,
	mauiDiagramPastePluginKey,
	type MauiDiagramPasteConfig,
} from './pm-plugins/maui-diagram-paste-plugin';
import {
	createMalleableUiPmPlugin,
	createToolbarSuppressionPlugin,
	malleableUiPluginKey,
} from './pm-plugins/plugin-key';
import { createRemixHistoryPmPlugin } from './pm-plugins/remix-history/remix-history-plugin';
import { createRemixHoverHighlightPmPlugin } from './pm-plugins/remix-hover-highlight-plugin';
import {
	createRemixThinkingPmPlugin,
	remixThinkingPluginKey,
} from './pm-plugins/remix-thinking/remix-thinking-plugin';
import { getRemixBlockMenuComponents } from './ui/block-menu/remix-block-menu-item';
import { MauiDiagramPasteFlag } from './ui/components/MauiDiagramPasteFlag';
import type { MauiEmbedSpacePickerProps } from './ui/components/MauiEmbedSpacePicker';
import { PersistedSelectionHighlightStyles } from './ui/components/PersistedSelectionHighlightStyles';
import { RemixContent } from './ui/components/RemixContent';
import { getPasteMenuItems } from './ui/paste-menu/get-paste-menu-items';
import { shouldShowRemixForSelectionContext } from './ui/remix-config';
import { makeRemixVariantFloatingToolbarButton } from './ui/toolbars/floating-toolbar/floating-toolbar-remix-variant';
import { getOverriddenFloatingToolbarConfig } from './ui/toolbars/overridden-floating-toolbar-config';
import { getSelectionToolbarComponents } from './ui/toolbars/toolbar-components';

const REMIX_BUTTON_DECORATION_TYPE = 'REMIX_BUTTON';

const LoadableMauiEmbedSpacePicker = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_editor-malleable-ui-maui-embed-space-picker" */ './ui/components/MauiEmbedSpacePicker'
		).then((mod) => mod.MauiEmbedSpacePicker) as Promise<
			React.ComponentType<React.PropsWithChildren<MauiEmbedSpacePickerProps>>
		>,
	loading: () => null,
});

export const malleableUiPlugin: MalleableUiPlugin = ({ api, config }) => {
	const isRovoLLMEnabled = config?.__isRovoLLMEnabled ?? false;
	const isRemixEnabled =
		isRovoLLMEnabled && expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true);
	const isRightSideEnabled = isRemixEnabled && fg('confluence_remix_button_right_side_block_fg');
	const remixDisabledReason = config?.__remixDisabledReason;
	const isRemixDisabled = !!remixDisabledReason;

	// Mutable reference to the editor view, set by a PM plugin on init.
	// Used by the Remix block-menu isHidden callback to compute text length.
	let editorView: EditorView | undefined;

	if (api && !config?.__isRemixHidden && isRemixEnabled) {
		api.toolbar?.actions.registerComponents(
			getSelectionToolbarComponents(api, { remixDisabledReason, isDisabled: isRemixDisabled }),
		);

		// Register Remix in the block menu when the experiment is enabled and
		// Remix is not disabled (e.g. restricted-content contexts).
		// Use NoExposure here since this runs on every editor load — exposure is
		// recorded when the block menu item is actually rendered.
		if (
			!isRemixDisabled &&
			expValEqualsNoExposure('platform_editor_remix_in_block_menu', 'isEnabled', true)
		) {
			api.blockMenu?.actions.registerBlockMenuComponents(
				getRemixBlockMenuComponents({ api, getEditorView: () => editorView }),
			);
		}

		if (
			!isRemixDisabled &&
			['hasSpellingAndGrammar', 'hasAltAiActions'].includes(
				expValNoExposure('platform_editor_paste_actions_menu_v2', 'variant', 'control'),
			)
		) {
			api.uiControlRegistry?.actions.register(getPasteMenuItems({ api }));
		}
	}

	if (api?.blockControls && !config?.__isRemixHidden && isRightSideEnabled) {
		const livePage = config?.__livePage;
		api.blockControls.actions.registerNodeDecoration({
			type: REMIX_BUTTON_DECORATION_TYPE,
			create: (params) =>
				remixButtonDecoration({
					...params,
					api,
					livePage,
					remixDisabledReason,
					isDisabled: isRemixDisabled,
					isRovoLLMEnabled,
				}),
			showInViewMode: true,
			shouldCreate: (params) => {
				const { editorState, rootPos } = params;
				if (rootPos === undefined) {
					return false;
				}
				const node = editorState.doc.nodeAt(rootPos);
				return shouldShowRemixForSelectionContext({ node: node ?? undefined });
			},
		});
	}

	return {
		name: 'malleableUi',

		pmPlugins() {
			const plugins = [
				{
					name: 'malleableUi',
					plugin: ({ dispatch }: { dispatch: Dispatch }) => createMalleableUiPmPlugin(dispatch),
				},
				{
					name: 'malleableUiEditorViewCapture',
					plugin: () =>
						createEditorViewCapturePlugin(
							(view) => {
								editorView = view;
							},
							() => {
								editorView = undefined;
							},
						),
				},
				{
					name: 'malleableUiToolbarSuppression',
					plugin: () => createToolbarSuppressionPlugin(),
				},
				{
					name: 'remixThinking',
					plugin: ({ dispatch, getIntl, nodeViewPortalProviderAPI }: PMPluginFactoryParams) =>
						createRemixThinkingPmPlugin({
							api,
							config,
							dispatch,
							getIntl,
							nodeViewPortalProviderAPI,
						}),
				},
				...(fg('cc-maui-phase-3')
					? [
							{
								name: 'remixHoverHighlight',
								plugin: () => createRemixHoverHighlightPmPlugin(),
							},
						]
					: []),
				...(fg('platform_editor_ai_remix_toolbar')
					? [
							{
								name: 'remixHistory',
								plugin: ({ dispatch }: PMPluginFactoryParams) =>
									createRemixHistoryPmPlugin({ api, dispatch }),
							},
						]
					: []),
			];

			if (config?.diagramPaste) {
				const diagramPasteConfig: MauiDiagramPasteConfig = {
					...config.diagramPaste,
					fireAnalyticsEvent: (payload) => {
						api?.analytics?.actions.fireAnalyticsEvent(payload as AnalyticsEventPayload);
					},
				};
				plugins.push({
					name: 'mauiDiagramPaste',
					plugin: ({ dispatch, getIntl }: PMPluginFactoryParams) =>
						createMauiDiagramPastePmPlugin(dispatch, diagramPasteConfig, getIntl),
				});
			}

			return plugins;
		},

		getSharedState(editorState): MalleableUiSharedState | undefined {
			if (!editorState) {
				return undefined;
			}
			const malleableUiState = malleableUiPluginKey.getState(editorState);
			const remixThinkingState = remixThinkingPluginKey.getState(editorState);
			if (!malleableUiState || !remixThinkingState) {
				return undefined;
			}
			return {
				aiEditReplacementLocalIds: malleableUiState.aiEditReplacementLocalIds,
				inFlightVariantUpdates: malleableUiState.inFlightVariantUpdates,
				isUpdate: remixThinkingState.isUpdate ?? false,
				historyEntryForUpdate: remixThinkingState.historyEntryForUpdate,
				dynamicUiSubtypeForUpdate: remixThinkingState.dynamicUiSubtypeForUpdate,
				isComplete: remixThinkingState.isComplete ?? false,
				isRemixModalOpen: malleableUiState.isRemixModalOpen,
				remixModalMetadata: malleableUiState.remixModalMetadata,
				isThinking: remixThinkingState.isThinking,
				remixSource: malleableUiState.remixSource,
				showLocationPicker: malleableUiState.showLocationPicker,
				diagramPaste: mauiDiagramPastePluginKey.getState(editorState),
				savedSelection: remixThinkingState.savedSelection,
			};
		},

		actions: {
			duplicateAndReplaceMauiApp: makeDuplicateAndReplaceMauiApp({ api, config }),
			finalizeMauiEmbedFromIndicator: makeFinalizeMauiEmbedFromIndicator({ config }),
			getRemixVariantFloatingToolbarButton: makeRemixVariantFloatingToolbarButton({ api }),
			isVariantUpdateInFlight: (id: string): boolean => {
				const sharedState = api?.malleableUi?.sharedState?.currentState();
				return sharedState?.inFlightVariantUpdates.has(id) ?? false;
			},
			shouldDisableRemix: () => {
				const malleableUiState = api?.malleableUi?.sharedState.currentState();
				return Boolean(
					malleableUiState?.isRemixModalOpen ||
					malleableUiState?.isThinking ||
					api?.aiExperience?.sharedState.currentState()?.areAiEntryPointsDisabled,
				);
			},
		},

		commands: {
			clearRemixThinkingDecoration,
			addBackendRemixHistoryStackEntry: makeAddBackendRemixHistoryStackEntryCommand,
			openRemixModal: makeOpenRemixModal(api),
			closeRemixModal: makeCloseRemixModal(api),
			startRemixThinking: makeStartRemixThinking(api),
			stopRemixThinking,
			replaceMauiApp,
			replaceWhiteboardEmbed,
			trackVariantUpdateStart: makeTrackVariantUpdateStartCommand,
			trackVariantUpdateComplete: makeTrackVariantUpdateCompleteCommand,
			updateRemixImageHistoryFieldId: makeUpdateRemixImageHistoryFieldIdCommand,
		},

		contentComponent: () => {
			return (
				<>
					<PersistedSelectionHighlightStyles />
					<RemixContent api={api} config={config} />
					{config?.diagramPaste && <MauiDiagramPasteFlag api={api} />}
					{config?.diagramPaste &&
					// eslint-disable-next-line @atlaskit/platform/use-recommended-utils -- dynamic config killswitch, not a standard feature gate
					!FeatureGates.getExperimentValue<boolean>(
						'maui-native-embed-finalize-diagram-anywhere',
						'value',
						false,
					) ? (
						<LoadableMauiEmbedSpacePicker api={api} config={config} />
					) : null}
				</>
			);
		},

		pluginsOptions:
			!config?.__isRemixHidden && !config?.__remixDisabledReason && isRemixEnabled
				? {
						floatingToolbar: getOverriddenFloatingToolbarConfig(api),
					}
				: {},
	};
};
